/**
 * Parse ingestion progress messages into structured thinking operations
 * Matches the prototype styling from test-thinking-mode.tsx
 */

export interface ParsedOperation {
  id: string
  type: 'search' | 'search_files' | 'read' | 'mcp_tool' | 'tool_call'
  label: string
  status: 'active' | 'completed' | 'error'
  result?: string
  timestamp?: string
}

/**
 * Extract operations from ingestion progress messages
 */
export function parseIngestionProgress(content: string): ParsedOperation[] {
  const operations: ParsedOperation[] = []
  const lines = content.split('\n')
  
  let currentStep: ParsedOperation | null = null
  
  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) continue
    
    // Match Step patterns: "📋 **Step 1:** Extracting frontmatter..."
    const stepMatch = trimmed.match(/^(\S+)\s+\*\*Step\s+(\d+):\*\*\s*(.+)$/)
    if (stepMatch) {
      const [, emoji, stepNum, label] = stepMatch
      if (currentStep) {
        operations.push({ ...currentStep, status: 'completed' })
      }
      currentStep = {
        id: `step-${stepNum}`,
        type: getOperationType(emoji, label),
        label: label.replace(/\*\*/g, '').trim(),
        status: 'active'
      }
      continue
    }
    
    // Match sub-operations: "  📄 Reading ingestion rules..."
    const subOpMatch = trimmed.match(/^\s+(\S+)\s+(.+)$/)
    if (subOpMatch) {
      const [, emoji, label] = subOpMatch
      if (currentStep) {
        // Complete current step
        operations.push({ ...currentStep, status: 'completed' })
        currentStep = null
      }
      operations.push({
        id: `op-${operations.length + 1}`,
        type: getOperationType(emoji, label),
        label: label.replace(/\*\*/g, '').trim(),
        status: 'active'
      })
      continue
    }
    
    // Match bullet points: "- Converting HTML to markdown"
    const bulletMatch = trimmed.match(/^[-•]\s*(.+)$/)
    if (bulletMatch) {
      const label = bulletMatch[1]
      if (currentStep) {
        // Add as sub-operation under current step
        operations.push({
          id: `sub-${operations.length + 1}`,
          type: getOperationType('', label),
          label: label.replace(/\*\*/g, '').trim(),
          status: 'active'
        })
      }
      continue
    }
    
    // Match result lines: "Found 0 similar todos, 0 recent project docs"
    const resultMatch = trimmed.match(/^(Found|Explored|Read|Called|Completed|✓)\s*(.+)$/i)
    if (resultMatch) {
      const [, prefix, result] = resultMatch
      if (operations.length > 0) {
        const lastOp = operations[operations.length - 1]
        if (lastOp.status === 'active') {
          lastOp.status = 'completed'
          lastOp.result = result.trim()
        }
      }
      continue
    }
  }
  
  // Complete any remaining active step
  if (currentStep) {
    operations.push({ ...currentStep, status: 'completed' })
  }
  
  return operations
}

/**
 * Determine operation type from emoji and label
 */
function getOperationType(emoji: string, label: string): ParsedOperation['type'] {
  const lowerLabel = label.toLowerCase()
  
  if (lowerLabel.includes('search') || emoji === '🔍') {
    return lowerLabel.includes('file') ? 'search_files' : 'search'
  }
  if (lowerLabel.includes('read') || emoji === '📄') {
    return 'read'
  }
  if (lowerLabel.includes('mcp') || lowerLabel.includes('tool')) {
    return 'mcp_tool'
  }
  if (lowerLabel.includes('call') || lowerLabel.includes('api')) {
    return 'tool_call'
  }
  
  return 'tool_call' // Default
}


/**
 * Check if a message is an ingestion progress message.
 *
 * We require the characteristic emoji+Step-marker format that ingestion agents
 * produce (e.g. "📋 **Step 1:** Extracting frontmatter") rather than matching
 * common English words that appear in normal assistant replies ("Step",
 * "Reading", "Generating", etc.).  The previous broad checks were causing
 * ordinary markdown responses to be routed through the ingestion-step UI,
 * which strips formatting on history reload.
 */
export function isIngestionProgressMessage(content: string): boolean {
  // Must contain the bold Step-N marker used by ingestion agents
  if (/\*\*Step\s+\d+[:.]/i.test(content)) return true
  // Very specific ingestion-only opening phrases (short, no overlap with normal prose)
  if (content.startsWith('No existing work spec')) return true
  if (content.startsWith('Pulled ') && content.includes('todo')) return true
  if (content.startsWith('Found an existing work spec')) return true
  return false
}

